import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "frontend-components/lib/utils";
import { Separator } from "frontend-components/components/ui/separator";

const buttonGroupVariants = cva(
    "flex w-fit items-stretch [&>*]:rounded-card [&>*]:focus-visible:relative [&>*]:focus-visible:z-10 [&>[data-slot=select-trigger]:not([class*='w-'])]:w-fit [&>input]:flex-1 has-[>[data-slot=button-group]]:gap-2",
    {
        variants: {
            orientation: {
                horizontal:
                    "[&>*:not(:first-child)]:rounded-l-none [&>*:not(:first-child)]:border-l-0 [&>*:not(:last-child)]:rounded-r-none",
                vertical:
                    "flex-col [&>*:not(:first-child)]:rounded-t-none [&>*:not(:first-child)]:border-t-0 [&>*:not(:last-child)]:rounded-b-none",
            },
        },
        defaultVariants: {
            orientation: "horizontal",
        },
    },
);

function ButtonGroup({
    className,
    orientation = "horizontal",
    ...props
}: React.ComponentProps<"div"> & VariantProps<typeof buttonGroupVariants>) {
    return (
        <div
            data-slot="button-group"
            data-orientation={orientation}
            role="group"
            className={cn(buttonGroupVariants({ orientation }), className)}
            {...props}
        />
    );
}

function ButtonGroupText({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="button-group-text"
            className={cn(
                "flex items-center gap-2 rounded-none border border-border bg-muted px-4 text-sm font-medium shadow-xs [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
                className,
            )}
            {...props}
        />
    );
}

function ButtonGroupSeparator({
    className,
    orientation = "vertical",
    ...props
}: React.ComponentProps<typeof Separator>) {
    return (
        <Separator
            data-slot="button-group-separator"
            orientation={orientation}
            className={cn("relative !m-0 self-stretch bg-input data-vertical:h-auto", className)}
            {...props}
        />
    );
}

export { ButtonGroup, ButtonGroupText, ButtonGroupSeparator, buttonGroupVariants };
